import React, { ChangeEvent, FC, Fragment, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Unstable_Grid2 as Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import { filter, includes } from 'lodash';

import { Buttons, Loaders, Tip } from 'components';
import { useSetBlocklistMutation } from 'api';
import { useGetBlocklistQuery } from 'api/public';
import { useCurrentUser, useNotify } from 'hooks';
import { NEGATIVE_REGEX } from 'app-constants';

import { FilterChip } from 'features/search/components/Filter';
import useUpdateBlocklist from 'features/admin/updaters/useUpdateBlocklist';

const Blocklist: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const toolTipRef = useRef(null);

  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading } = useGetBlocklistQuery({ client_id: currentUser.client_id });
  const terms = data?.getBlockList?.terms || [];

  const mutationOptions = useUpdateBlocklist();
  const { mutate } = useSetBlocklistMutation({
    ...mutationOptions,
    onSuccess,
  });

  function onSuccess() {
    setInput('');
    notify.info({ message: t('admin:blocklistSuccess') });
  }

  function onClickSave() {
    const list = input.split(',').filter(term => term.trim() !== '');
    if (list.length > 1000) {
      setError(t('admin:phraseCountError'));
      return;
    }

    if (list.some(term => term.split(' ').length > 50)) {
      setError(t('admin:wordCountError'));
      return;
    }

    if (!list.every(term => !term.match(NEGATIVE_REGEX))) {
      setError(t('admin:specialCharError'));
      return;
    }

    if (list.some(term => term.length < 3)) {
      setError(t('admin:shortCharError'));
      return;
    }

    const lowerCaseInputs = list.map(term => term.toLowerCase().trimStart());
    const lowerCaseBlockLists = terms.map((term: string) => term.toLowerCase().trimStart());
    if (lowerCaseInputs.filter(term => lowerCaseBlockLists.includes(term)).length) {
      setError(t('admin:duplicateError'));
      return;
    }

    const duplicates = filter(lowerCaseInputs, (term, idx, iteratee) =>
      includes(iteratee, term, idx + 1)
    );
    if (duplicates.length) {
      setError(t('admin:duplicateInputError'));
      return;
    }
    const trimmedList = list.map(term => term.trimStart());

    mutate({ terms: terms.concat(trimmedList) });
  }

  function onDeleteChip(term: string) {
    mutate({ terms: terms.filter(t => term !== t) });
  }

  function onClickClearAll() {
    mutate({ terms: [] });
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    setError('');
    setInput(event.target.value);
  }

  function renderActiveTerms() {
    if (isLoading) {
      return (
        <Fragment>
          <Loaders.Standard />
          <Box minHeight={200} />
        </Fragment>
      );
    }

    if (!terms.length) {
      return null;
    }

    return (
      <Fragment>
        <Grid container>
          <Grid xs={12}>
            <Divider sx={{ margin: theme.spacing(3, 0) }} />
          </Grid>

          <Grid xs={2}>
            <Typography>{t('admin:activeTerms')}</Typography>
          </Grid>

          <Grid xs={8} container spacing={1}>
            {terms.map((term, idx) => (
              <Grid key={term + idx}>
                <Tooltip title={term.length >= 25 ? term : ''} ref={toolTipRef}>
                  <FilterChip
                    sx={{
                      borderRadius: 3,
                      minWidth: 30,
                    }}
                    label={term}
                    onDelete={() => onDeleteChip(term)}
                  />
                </Tooltip>
              </Grid>
            ))}
          </Grid>

          <Grid xs={2} container justifyContent="flex-end">
            <Grid>
              <Button
                sx={{ borderRadius: 3 }}
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={onClickClearAll}
              >
                {t('components:clearAll')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Box minHeight={200} />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Typography>{t('admin:blocklistHelp')}</Typography>
      <Tip i18nKey="admin:blocklistTip" />
      <Divider sx={{ margin: theme.spacing(3, 0) }} />

      <Grid container>
        <Grid xs={2}>
          <Typography variant="h6">{t('admin:addTerms')}</Typography>
        </Grid>
        <Grid xs={10}>
          <FormControl variant="standard" error={!!error} fullWidth>
            <TextField
              variant="outlined"
              fullWidth
              multiline
              rows="6"
              maxRows="30"
              placeholder={t('admin:blocklistPlaceholder')}
              value={input}
              onChange={onChange}
              error={!!error}
            />
            {!!error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        </Grid>
      </Grid>

      <Grid container justifyContent="flex-end">
        <Grid>
          <Box marginTop={1}>
            <Buttons.Primary disabled={!input || !!error} onClick={onClickSave}>
              {t('common:save')}
            </Buttons.Primary>
          </Box>
        </Grid>
      </Grid>

      {renderActiveTerms()}
    </Fragment>
  );
};

export default Blocklist;
